import { MenuItemOfBag } from '@app/contexts/Bag';
import { Address, Complement, ComplementGroup, DeliveryTypesAvailable, Restaurant, User } from '@app/types';
import { MenuItemTypeGuard } from '@app/types/menu-item';
import { format } from 'date-fns';
import { getFullAddressText } from './address';
import { calculateGroupPrice, shouldApplyCalculusByFlavourMoreExpensive } from './calculus';
import { getBRCurrencyFormat, UserFormatter } from './formmater';
import { getComplementsSelectedOf } from './menu';
import { KEY_PIX_MASKS } from './payments';

const OrderMessageUtils = {
  shouldDisplayDeliveryTax(order: OrderPayload) {
    return order.deliveryType === DeliveryTypesAvailable.HomeDelivery && order.deliveryTax;
  },
  existsSomeComplementSelected(group: ComplementGroup) {
    return group.complementos.some((complement) => (complement.amount ?? 0) > 0);
  },
  getDeliveryTypeTitle(order: OrderPayload) {
    if (order.tableNumber) return 'MESA';
    else if (order.counterNumber) return 'BALCÃO';
    else if (order.deliveryType === DeliveryTypesAvailable.HomeDelivery) return 'ENTREGA';
    else return 'RETIRADA';
  },
  getItemsByMenuName(items: MenuItemOfBag[]) {
    const menus: { [key: string]: MenuItemOfBag[] } = {};
    for (const item of items) {
      const menuName = (MenuItemTypeGuard.isFavoritedOfBag(item) ? item.originalMenuName : item.menuName) ?? '';
      if (!menus[menuName]) menus[menuName] = [item];
      else menus[menuName].push(item);
    }
    return menus;
  },
};

export type Coords = {
  latitude: number;
  longitude: number;
};

export type OrderPayload = {
  items: MenuItemOfBag[];
  restaurant: Restaurant;
  orderDate: number;
  address: Address;
  payment: string;
  changeValue: number;
  changeIsNecessary: boolean;
  deliveryType: DeliveryTypesAvailable;
  totalOrder: number;
  deliveryTax: null | number;
  tableNumber?: number;
  counterNumber?: number;
};

export function getWhatsappOrderMessage(order: OrderPayload, user: User | null) {
  const menus = OrderMessageUtils.getItemsByMenuName(order.items);

  let itemsText = '';
  for (const menuName of Object.keys(menus)) {
    itemsText += `\n*${menuName.toUpperCase()}*\n`;
    const items = menus[menuName];
    let isFirstItem = true;
    for (const item of items || []) {
      const itemAmount = item.amount ?? 1;
      const priceItem = item.preco ? `(${getBRCurrencyFormat(item.preco * itemAmount)})` : '';

      if (isFirstItem) {
        itemsText += `\`\`\`${item.amount} ${item.nome} ${priceItem}\`\`\`\n`;
        isFirstItem = false;
      } else {
        itemsText += `\n\`\`\`${item.amount} ${item.nome} ${priceItem}\`\`\`\n`;
      }
      for (const group of item.gruposDeComplementos || []) {
        if (!OrderMessageUtils.existsSomeComplementSelected(group)) {
          continue;
        }
        const groupText = getGroupText(group);
        itemsText += groupText;
      }

      if (item.observations) {
        itemsText += `*Observações:*\n_${item.observations}_`;
        const isLastItem = item.id === items[items.length - 1].id;
        if (isLastItem) {
          itemsText += '\n';
        }
      }
    }
  }

  let message = createTitle();
  if (user) message += createUserData(user);

  if (order.tableNumber) {
    message += '\n\n*Tipo de Entrega*: Pedido na Mesa';
    message += `\n\n*Mesa*: ${order.tableNumber}`;
  } else if (order.counterNumber) {
    message += '\n\n*Tipo de Entrega*: Pedido no Balcão';
    message += `\n\n*Balcão*: ${order.counterNumber}`;
  } else {
    message += createPaymentTypeLabel();
    message += createChangeLabel();
    message += createDeliveryTypeLabel();

    if (order.deliveryType === DeliveryTypesAvailable.HomeDelivery) {
      const addressLabel = `*Endereço de Entrega:* ${getFullAddressText(order.address)}`;

      const openInMapLabel = `*Link do Endereço para o Entregador:* https://www.google.com/maps/dir//${encodeURIComponent(
        getFullAddressText(order.address)
      )}`;

      if (OrderMessageUtils.shouldDisplayDeliveryTax(order)) {
        message += `\n\n*Taxa de Entrega*: ${getBRCurrencyFormat(order.deliveryTax as number)}`;
      }
      message += `\n\n${addressLabel}\n\n`;
      message += openInMapLabel;
    }
  }

  message += `\n${itemsText}`;
  if (order.totalOrder) {
    message += `\n\n*Valor Total:* ${getBRCurrencyFormat(order.totalOrder)}`;
  }

  return message;

  function createChangeLabel() {
    const shouldDisplayChangeLabel = order.deliveryType === 'Entregar no Endereço' && order.payment === 'Dinheiro';
    if (!shouldDisplayChangeLabel) return '';

    let changeText = '';
    if (!order.changeIsNecessary) {
      changeText = 'Não precisa de troco';
    } else if (order.changeValue) {
      changeText = getBRCurrencyFormat(order.changeValue);
    } else {
      changeText = 'Valor de troco não informado';
    }

    return `\n\n*Troco para:* ${changeText}`;
  }

  function getGroupText(group: ComplementGroup) {
    const applyPizzaCalculus = shouldApplyCalculusByFlavourMoreExpensive(group);

    let groupTitle = '';
    if (applyPizzaCalculus) {
      const groupPrice = getBRCurrencyFormat(calculateGroupPrice(group));
      groupTitle = `*${group.nome}* (${groupPrice})\n`;
    } else {
      groupTitle = `*${group.nome}*\n`;
    }

    const complementsSelected = getComplementsSelectedOf(group);
    let groupComplementsText = '';
    if (applyPizzaCalculus) {
      groupComplementsText = complementsSelected.map(createComplementLabel).join('');
    } else {
      groupComplementsText = complementsSelected.map(createComplementLabelWithPrice).join('');
    }

    return groupTitle + groupComplementsText;

    function createComplementLabel(complement: Complement) {
      if ((complement.amount ?? 1) > 1) {
        return `---- \`\`\`${complement.amount}${complement.nome}\`\`\`\n`;
      }
      return `---- ${complement.amount}${complement.nome}\n`;
    }

    function createComplementLabelWithPrice(complement: Complement) {
      const complementAmount = complement.amount ?? 1;
      const amount = complementAmount > 1 ? `${complementAmount} ` : '';
      const price = complement.preco > 0 ? `(${getBRCurrencyFormat(complement.preco * complementAmount)})` : '';
      if (complementAmount > 1) {
        return `---- \`\`\`${amount}${complement.nome}${price ? ' ' + price : ''}\`\`\`\n`;
      }
      return `---- ${amount}${complement.nome}${price ? ' ' + price : ''}\n`;
    }
  }

  function createPaymentTypeLabel() {
    if (!order.payment) {
      return '';
    }

    const keyPIXType = order.restaurant?.Configuracoes?.tipoDaChavePIX ?? '';
    const keyPIX = order.restaurant?.Configuracoes?.chavePIX ?? '';

    let label = '';
    if (order.payment === 'PIX') {
      const mask = KEY_PIX_MASKS[keyPIXType];
      label = `PIX (${mask ? mask.resolve(keyPIX) : keyPIX})`;
    } else {
      label = order.payment;
    }

    return `\n\n*Forma de Pagamento*: ${label}`;
  }

  function createDeliveryTypeLabel() {
    if (!order.deliveryTax) return '';
    return `\n\n*Tipo de Entrega*: ${order.deliveryType}`;
  }

  function createTitle() {
    const formattedDate = format(new Date(order.orderDate), "dd/MM/yyyy 'às' HH:mm'h'");
    const deliveryTypeTitle = OrderMessageUtils.getDeliveryTypeTitle(order);
    return `*CLIQUEI ACHEI PEDIDOS (${deliveryTypeTitle})*\n\n${formattedDate}`;
  }

  function createUserData(_user: User) {
    let result = `\n\n*Cliente*: ${_user.name}`;
    result += `\n\n*Telefone do Comprador:* ${UserFormatter.getFormattedPhone(_user.cellphone)}`;
    return result;
  }
}

export function generateWhatsappLink(phone: string, message: string) {
  const encodedMessageText = encodeURI(message);
  return `https://wa.me/55${phone}?text=${encodedMessageText}`;
}
